var _a;
import Display from './Display';
import Position from './Position';
import * as PositionUtil from './PositionUtil';
import * as AnimateUtil from '../../util/AnimateUtil';
import { ltjsGlobalNamespace } from '../../ltjsInit';
var popoverIds = (_a = ltjsGlobalNamespace.__popoverIds) !== null && _a !== void 0 ? _a : new Map();
ltjsGlobalNamespace.__popoverIds = popoverIds;
function _clamp(min, max, value) {
    // NOTE(mikcol): keeping this impl for legacy behavior
    return Math.min(Math.max(min, value), max);
}
function _updateContainerDisplay(container, display) {
    if (container) {
        if (display === Display.NONE) {
            container.classList.add('sasInvisible');
        }
        else {
            container.classList.remove('sasInvisible');
        }
    }
}
function _getArrowContainerMetrics(container, parentContainer) {
    if (container) {
        var styles = getComputedStyle(container);
        var bounds = parentContainer.getBoundingClientRect();
        return {
            padding: {
                left: parseFloat(styles.paddingLeft),
                right: parseFloat(styles.paddingRight),
                top: parseFloat(styles.paddingTop),
                bottom: parseFloat(styles.paddingBottom),
            },
            width: bounds.width,
            height: bounds.height,
        };
    }
    // NOTE(mikcol): added return to align with legacy
    return undefined;
}
// This is a combination of what popper.js does and layout.ts from nova-commons.
//   https://github.com/floating-ui/floating-ui/blob/master/packages/dom/src/utils/getViewportRect.ts
//   https://gitlab.sas.com/novacommons/nova-commons/-/blob/master/src/nova-commons/src/layout.ts#L125
function getViewportRect(element) {
    var _a;
    // FIXME: older lib.dom.d.ts does't have visualViewport
    var win = ((_a = element.ownerDocument.defaultView) !== null && _a !== void 0 ? _a : window);
    var viewport = win.visualViewport;
    if (viewport) {
        return {
            width: viewport.width,
            height: viewport.height,
            top: viewport.offsetTop,
            left: viewport.offsetLeft,
        };
    }
    //Fallback
    var doc = element.ownerDocument;
    if (doc.doctype !== null && doc.documentElement) {
        return {
            width: doc.documentElement.clientWidth,
            height: doc.documentElement.clientHeight,
            top: 0,
            left: 0,
        };
    }
    // Unlikely fallback. Missing doctype (probably a bug) and outdated browser.
    return {
        width: win.innerWidth,
        height: win.innerHeight,
        top: 0,
        left: 0,
    };
}
var Popover = /** @class */ (function () {
    function Popover() {
        this._currentAnimation = null;
        this._positions = [Position.TOP, Position.BOTTOM]; // TODO(legacy): RIGHT, LEFT
        this._currentPosition = -1;
        this._classNames = ['sasPopover'];
        this._display = Display.VISIBLE;
        this._showArrow = true;
        this._contentId = null;
        this._styleOverrides = null;
        this._currentTarget = null;
        this._currentConstraint = null;
        this.__type = 'sas.ltjs.commons.ui.popover.Popover';
        this._nextContentId();
    }
    Popover.prototype.setNamespace = function (value) {
        this.__type = value;
        this._nextContentId();
    };
    /**
     * Simple algorithm for generating unique ids for the rendered popover.
     * @private
     */
    Popover.prototype._nextContentId = function () {
        var _a;
        var renderName = 'Popover';
        var typeIndex = this.__type.lastIndexOf('.');
        if (typeIndex !== -1) {
            renderName = this.__type.substring(typeIndex + 1);
        }
        var id = (_a = popoverIds.get(renderName)) !== null && _a !== void 0 ? _a : 0;
        popoverIds.set(renderName, id + 1);
        this._contentId = renderName + id;
    };
    /**
     * The dom reference for the Popover.
     * @param {string} [selector] selects a child of the current popover if one exists.
     * @returns {HTMLElement | null} DOM Element for the selected item.
     */
    Popover.prototype.getDomRef = function (selector) {
        var container = this._contentId ? document.getElementById(this._contentId) : null;
        if (!container) {
            return null;
        }
        return selector ? container.querySelector(selector) : container;
    };
    /**
     * Query's the dom element associated with this Popover to find all of the children
     * that matches the provided selector.
     * @param {string} selector selects the children of the current popover if one exists.
     * @returns {NodeList<Element> | null} DOM Elements for the selected items.
     */
    Popover.prototype.getDomRefs = function (selector) {
        var container = this._contentId ? document.getElementById(this._contentId) : null;
        if (!container) {
            return null;
        }
        return container.querySelectorAll(selector);
    };
    Popover.prototype.getDisplay = function () {
        return this._display;
    };
    /**
     * sets the css display of the popup. Handles animations.
     * @param {Display} value the updated display value.
     * @param {boolean} animate if the display update should be animated.
     */
    Popover.prototype.setDisplay = function (value, animate) {
        var _this = this;
        if (value !== this._display) {
            this._display = value;
            var container_1 = this.getDomRef();
            if (!container_1) {
                return;
            }
            if (animate || animate === undefined) {
                if (value === Display.VISIBLE) {
                    if (this._currentAnimation) {
                        this._currentAnimation.cancel();
                        this._currentAnimation = null;
                    }
                    _updateContainerDisplay(container_1, value);
                    container_1.style.opacity = '1';
                }
                else {
                    this._currentAnimation = AnimateUtil.animate(container_1, 'opacity', 0, function () {
                        _this._currentAnimation = null;
                        _updateContainerDisplay(container_1, value);
                    });
                }
            }
            else {
                _updateContainerDisplay(container_1, value);
            }
        }
    };
    Popover.prototype.getShowArrow = function () {
        return this._showArrow;
    };
    Popover.prototype.setShowArrow = function (value) {
        this._showArrow = value;
    };
    Popover.prototype.getCurrentPosition = function () {
        return this._currentPosition;
    };
    Popover.prototype.resetCurrentPosition = function () {
        this._currentPosition = -1;
    };
    Popover.prototype.getStyleOverrides = function () {
        return this._styleOverrides;
    };
    Popover.prototype.setStyleOverrides = function (value) {
        if (this._styleOverrides === value) {
            return;
        }
        this._styleOverrides = value;
        if (!value) {
            this.removeCssStyleVariables();
        }
        else if (this.getDisplay() === Display.VISIBLE) {
            this.setCssStyleVariables();
            this.positionPopover(this._currentTarget, this._currentConstraint);
        }
    };
    /**
     * @protected
     */
    Popover.prototype._resetContent = function (_target, _constraint) { };
    /**
     * @protected
     */
    Popover.prototype._sizeContent = function () { };
    Popover.prototype.setPositions = function (positions) {
        this._positions = positions;
    };
    /**
     * Positions popup around a target in relation to the constrained size.
     *     The target and constraint can be an Element or Object.
     *     If constraint is an object, then it assumes that these are global coordinates with the properties:
     *         {
     *             left: Number,
     *             top: Number,
     *             width: Number,
     *             height: Number
     *         }
     * @param {(Element|PositionUtil.BoxParameters)} target [description]
     * @param {(Element|PositionUtil.BoxParameters)} constraint Optional. The constraints with which the popoup should be positioned inside. If constraint is null then the document body is used.
     */
    Popover.prototype.positionPopover = function (target, constraint, reusePosition) {
        var container = this.getDomRef();
        if (!container) {
            return;
        }
        var positions = this._positions;
        if (reusePosition && this._currentPosition !== -1) {
            positions = this._positions.concat();
            var currentPosition = positions.splice(positions.indexOf(this._currentPosition), 1)[0];
            positions.unshift(currentPosition);
        }
        var arrow = container.querySelector('.sasPopoverArrow');
        var arrowContainer = container.querySelector('.sasArrowContainer');
        var oldDisplay = this.getDisplay();
        if (oldDisplay === Display.NONE) {
            this.setDisplay(Display.VISIBLE, false);
        }
        // stash state for applying style overrides while visible
        this._currentTarget = target;
        this._currentConstraint = constraint;
        //reset position for sizing;
        var containerStyle = container.style;
        containerStyle.left = '0';
        containerStyle.top = '0';
        containerStyle.maxWidth = '';
        containerStyle.maxHeight = '';
        arrow.style.left = '';
        arrow.style.top = '';
        // IE does not support multiple arguments for remove.
        container.classList.remove('sasPositionTop');
        container.classList.remove('sasPositionLeft');
        container.classList.remove('sasPositionRight');
        container.classList.remove('sasPositionBottom');
        var arrowHeight = arrowContainer.getBoundingClientRect().height;
        arrowContainer.style.display = 'none';
        this._resetContent(target, constraint);
        var positionConstraint = constraint !== null && constraint !== void 0 ? constraint : getViewportRect(container);
        var position = PositionUtil.position(container, target, positionConstraint, positions, arrowHeight);
        if (position) {
            containerStyle.left = String(position.offset.left) + 'px';
            containerStyle.top = String(position.offset.top) + 'px';
            containerStyle.maxWidth = String(position.fitPosition.maxWidth) + 'px';
            containerStyle.maxHeight = String(position.fitPosition.maxHeight) + 'px';
            this._sizeContent();
            position = PositionUtil.position(container, target, positionConstraint, positions, arrowHeight);
            containerStyle.left = String(position.offset.left) + 'px';
            containerStyle.top = String(position.offset.top) + 'px';
            containerStyle.maxWidth = String(position.fitPosition.maxWidth) + 'px';
            containerStyle.maxHeight = String(position.fitPosition.maxHeight) + 'px';
            var arrowHorizontal = true;
            if (position.fit === Position.TOP) {
                container.classList.add('sasPositionTop');
            }
            else if (position.fit === Position.LEFT) {
                container.classList.add('sasPositionLeft');
                arrowHorizontal = false;
            }
            else if (position.fit === Position.RIGHT) {
                container.classList.add('sasPositionRight');
                arrowHorizontal = false;
            }
            else {
                container.classList.add('sasPositionBottom');
            }
            if (this._showArrow) {
                arrowContainer.style.display = '';
            }
            // Shift arrow so that it is still in the center of the target
            var arrowContMetrics = void 0, arrowBounds = void 0;
            if (arrowHorizontal) {
                arrowContMetrics = _getArrowContainerMetrics(arrowContainer, container);
                arrowBounds = arrow.getBoundingClientRect();
                var offsetLeft = position.fitPosition.left - position.offset.left;
                offsetLeft = offsetLeft + arrowContMetrics.width / 2;
                offsetLeft = _clamp(arrowContMetrics.padding.left + arrowBounds.width / 2, arrowContMetrics.width - arrowBounds.width / 2 - arrowContMetrics.padding.right, offsetLeft);
                arrow.style.left = String(offsetLeft) + 'px';
            }
            else {
                arrowContMetrics = _getArrowContainerMetrics(arrowContainer, container);
                arrowBounds = arrow.getBoundingClientRect();
                var offsetTop = position.fitPosition.top - position.offset.top;
                offsetTop = offsetTop + arrowContMetrics.height / 2;
                offsetTop = _clamp(5 + arrowBounds.height / 2, arrowContMetrics.height - arrowBounds.height / 2 - 5, offsetTop);
                arrow.style.top = String(offsetTop) + 'px';
            }
            this._currentPosition = position.fit;
        }
        else {
            this._currentPosition = -1;
        }
        if (this._showArrow) {
            arrowContainer.style.display = '';
        }
        this.setDisplay(oldDisplay, false);
    };
    /**
     * The rendered contents  of the Popopver. Overridden by child classes.
     * @returns {HTMLElement} Dom element of the popover content.
     * @protected
     */
    Popover.prototype._getPopoverContent = function () {
        return null;
    };
    Popover.prototype.renderPopover = function (parentElement, zIndex) {
        var container, contentContainer;
        if (this._contentId) {
            container = document.getElementById(this._contentId);
            if (!container) {
                this.removeCssStyleVariables();
                this._contentId = null;
            }
        }
        if (!this._contentId) {
            this._nextContentId();
            container = document.createElement('div');
            container.id = this._contentId;
            var classList = container.classList;
            for (var i = 0, len = this._classNames.length; i < len; ++i) {
                classList.add(this._classNames[i]);
            }
            var arrow = document.createElement('div');
            arrow.classList.add('sasPopoverArrow');
            var arrowContainer = document.createElement('div');
            arrowContainer.classList.add('sasArrowContainer');
            arrowContainer.appendChild(arrow);
            container.appendChild(arrowContainer);
            contentContainer = document.createElement('div');
            contentContainer.classList.add('sasPopoverContent');
            container.appendChild(contentContainer);
        }
        container = container;
        if (zIndex) {
            container.style.zIndex = String(zIndex);
        }
        contentContainer = contentContainer || container.querySelector('.sasPopoverContent');
        contentContainer.innerHTML = '';
        var content = this._getPopoverContent();
        if (content) {
            contentContainer.appendChild(content);
        }
        _updateContainerDisplay(container, this._display);
        if (parentElement) {
            parentElement.appendChild(container);
        }
        this.setCssStyleVariables();
        return container;
    };
    Popover.prototype.setCssStyleVariables = function () { };
    Popover.prototype.removeCssStyleVariables = function () { };
    Popover.prototype.removePopover = function () {
        var container = this.getDomRef();
        if (!container) {
            return;
        }
        var parent = container.parentNode;
        if (parent) {
            parent.removeChild(container);
        }
        this._contentId = null;
    };
    Popover.prototype.destroy = function () {
        this.removeCssStyleVariables();
        this.removePopover();
        this._currentTarget = null;
        this._currentConstraint = null;
    };
    return Popover;
}());
export default Popover;
